// ============================================================
// Storefront hostname → tenant routing helpers
// ============================================================
// Pure functions, no IO. Shared between the public storefront app
// (resolves which tenant to load at boot) and the admin app
// (validates subdomains and custom domains).
// ============================================================

/** Subdomains that must never be used by a tenant. They're either:
 *  - Used by the platform itself (`dashboard`, `api`, `app`, `gesty`, `landing`, `lyfty`)
 *  - DNS conventions (`www`, `mail`, `smtp`, `imap`, `pop`, `ftp`)
 *  - CDN / infra (`cdn`, `static`, `assets`, `media`)
 *  - Likely future-reserved (`admin`, `account`, `auth`, `support`,
 *    `help`, `docs`, `blog`, `status`, `staging`, `dev`, `test`)
 */
export const RESERVED_SUBDOMAINS = new Set<string>([
  'admin', 'api', 'app', 'account', 'accounts', 'assets', 'auth',
  'blog', 'cdn', 'dashboard', 'dev', 'docs', 'email', 'ftp', 'gesty',
  'help', 'imap', 'landing', 'lyfty', 'mail', 'media', 'pop', 'shop',
  'smtp', 'staging', 'static', 'status', 'store', 'support', 'test',
  'web', 'www',
]);

/** Subdomain validation rules — kept in sync with what most DNS providers
 *  and Cloudflare Pages will accept. */
export const SUBDOMAIN_PATTERN = /^[a-z0-9]([a-z0-9-]{1,28}[a-z0-9])?$/;
export const SUBDOMAIN_MIN_LENGTH = 3;
export const SUBDOMAIN_MAX_LENGTH = 30;

export type SubdomainValidation =
  | { ok: true }
  | { ok: false; reason: 'too_short' | 'too_long' | 'invalid_format' | 'reserved' };

/** Returns ok or the specific failure reason. Intentionally machine-readable
 *  so the caller decides how to phrase the message. */
export function validateSubdomain(input: string): SubdomainValidation {
  const v = input.trim().toLowerCase();
  if (v.length < SUBDOMAIN_MIN_LENGTH) return { ok: false, reason: 'too_short' };
  if (v.length > SUBDOMAIN_MAX_LENGTH) return { ok: false, reason: 'too_long' };
  if (!SUBDOMAIN_PATTERN.test(v)) return { ok: false, reason: 'invalid_format' };
  if (RESERVED_SUBDOMAINS.has(v)) return { ok: false, reason: 'reserved' };
  return { ok: true };
}

/** Lowercase + trim. Returns null when the input is empty/blank — useful for
 *  storing in the DB where empty strings should become NULL. */
export function normalizeSubdomain(input: string | null | undefined): string | null {
  if (!input) return null;
  const v = input.trim().toLowerCase();
  return v.length === 0 ? null : v;
}

/** Strip protocol/path/query and lowercase. Returns null on empty input.
 *  Does NOT validate that the host is a real domain (DNS check happens
 *  separately). */
export function normalizeCustomDomain(input: string | null | undefined): string | null {
  if (!input) return null;
  let v = input.trim().toLowerCase();
  if (!v) return null;
  v = v.replace(/^https?:\/\//, '');
  v = v.replace(/\/.*$/, '');
  v = v.replace(/\?.*$/, '');
  v = v.replace(/#.*$/, '');
  // Strip trailing dot from FQDNs and any leading dots someone pasted by accident.
  v = v.replace(/^\.+/, '').replace(/\.+$/, '');
  return v.length === 0 ? null : v;
}

/** Result of resolving a request hostname to its routing intent. */
export type StorefrontHostnameMatch =
  /** Tenant identified by their <sub>.<rootDomain> subdomain. */
  | { kind: 'subdomain'; subdomain: string }
  /** Tenant identified by their custom domain (anything that's not on rootDomain). */
  | { kind: 'custom'; hostname: string }
  /** The request hit the platform root itself (eg. gesty.tn or www.gesty.tn)
   *  — the storefront should show a "no tenant here" page or marketing site. */
  | { kind: 'platform_root' }
  /** Local development or unrecognized host — the app picks a fallback strategy. */
  | { kind: 'unknown' };

/** Parse the request's hostname into a routing decision. Pure, deterministic.
 *
 *  - `host`         the full hostname (no port, no scheme)
 *  - `rootDomain`   the platform's own root(s) — a string, or an array to
 *                   match several at once, eg. ["gesty.tn", "lyfty.agency"]
 *                   during a domain migration
 *
 *  Examples (rootDomain = "gesty.tn"):
 *    tunielec.gesty.tn      → { kind: 'subdomain', subdomain: 'tunielec' }
 *    www.tunielec.tn            → { kind: 'custom', hostname: 'www.tunielec.tn' }
 *    gesty.tn               → { kind: 'platform_root' }
 *    www.gesty.tn           → { kind: 'platform_root' }
 *    localhost                  → { kind: 'unknown' }
 *    127.0.0.1                  → { kind: 'unknown' }
 */
export function parseStorefrontHostname(
  host: string,
  rootDomain: string | readonly string[],
): StorefrontHostnameMatch {
  const h = host.trim().toLowerCase().replace(/\.+$/, '');
  if (!h) return { kind: 'unknown' };

  // Local / IP — let the app fall back to env-configured tenant
  if (h === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(h) || h.endsWith('.local')) {
    return { kind: 'unknown' };
  }

  // One or more platform roots. Accepting several supports a domain
  // migration: while gesty.tn and the legacy lyfty.agency are both live,
  // <sub>.gesty.tn AND <sub>.lyfty.agency must resolve to the same tenant.
  // The first root that matches wins.
  const roots = (typeof rootDomain === 'string' ? [rootDomain] : rootDomain)
    .map((r) => r.trim().toLowerCase().replace(/^\.+/, '').replace(/\.+$/, ''))
    .filter((r) => r.length > 0);

  for (const root of roots) {
    // Apex of a platform root itself
    if (h === root) return { kind: 'platform_root' };

    // <sub>.<root>
    if (h.endsWith('.' + root)) {
      const subPart = h.slice(0, h.length - root.length - 1);
      // www on the root → still platform root (we don't lease "www" as a tenant)
      if (subPart === 'www') return { kind: 'platform_root' };
      // Multi-level subdomain (eg. shop.tenant.gesty.tn) — keep the leftmost label
      // as the canonical subdomain. We could also reject these; for now we accept and
      // resolve by the FIRST label.
      const sub = subPart.split('.')[0];
      if (!sub) return { kind: 'platform_root' };
      return { kind: 'subdomain', subdomain: sub };
    }
  }

  // Anything else → custom domain
  return { kind: 'custom', hostname: h };
}
